/**
 * NewDealDialog
 * 
 * Modal dialog for creating a new Deal for MarketFit analysis.
 * Allows setting deal name, selecting/creating a solution, and selecting/creating a situation.
 * The customer actor will be selected within the analysis page itself.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, message, Spin, Divider, Button } from 'antd';
import { AimOutlined, PlusOutlined, SolutionOutlined, BulbOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import styles from './NewDealDialog.module.css';

const { TextArea } = Input;

interface NewDealDialogProps {
  visible: boolean;
  productId: string;
  onCancel: () => void;
  onSuccess: (dealId: string, dealName: string) => void;
}

interface DealFormValues {
  name: string;
  description?: string;
  solutionId?: string;
  solutionName?: string;
  situationId?: string;
  situationName?: string;
}

interface SelectOption {
  value: string;
  label: string;
}

// Artifact type IDs
const DEAL_ARTIFACT_TYPE = 'Deal';
const SOLUTION_ARTIFACT_TYPE = 'ProductSolution';
const SITUATION_ARTIFACT_TYPE = 'CustomerSituation';

export const NewDealDialog: React.FC<NewDealDialogProps> = ({
  visible,
  productId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm<DealFormValues>();
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [solutions, setSolutions] = useState<SelectOption[]>([]);
  const [situations, setSituations] = useState<SelectOption[]>([]);
  const [createNewSolution, setCreateNewSolution] = useState(false);
  const [createNewSituation, setCreateNewSituation] = useState(false);

  // Fetch existing solutions and situations
  const fetchOptions = useCallback(async () => {
    if (!visible) return;
    
    setLoadingOptions(true);
    try {
      // Fetch solutions
      const solutionsRes = await apiClient.get(`/artifacts`, {
        params: { artifact_type: SOLUTION_ARTIFACT_TYPE, company_id: productId }
      });
      const solutionList = Array.isArray(solutionsRes.data) 
        ? solutionsRes.data 
        : solutionsRes.data?.artifacts || [];
      setSolutions(solutionList.map((s: { id: string; name?: string }) => ({
        value: s.id,
        label: s.name || 'Unnamed Solution',
      })));

      // Fetch situations
      const situationsRes = await apiClient.get(`/artifacts`, {
        params: { artifact_type: SITUATION_ARTIFACT_TYPE, company_id: productId }
      });
      const situationList = Array.isArray(situationsRes.data) 
        ? situationsRes.data 
        : situationsRes.data?.artifacts || [];
      setSituations(situationList.map((s: { id: string; name?: string }) => ({
        value: s.id,
        label: s.name || 'Unnamed Situation',
      })));
    } catch (error) {
      console.error('Failed to fetch options:', error);
      // Not fatal - user can still create new ones
    } finally {
      setLoadingOptions(false);
    }
  }, [visible, productId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      let solutionId = values.solutionId;
      let situationId = values.situationId;

      // Create new solution if needed
      if (createNewSolution && values.solutionName) {
        try {
          const res = await apiClient.post('/artifacts', {
            name: values.solutionName,
            artifact_type: SOLUTION_ARTIFACT_TYPE,
            company_id: productId,
            artifact_metadata: {},
          });
          solutionId = res.data.id;
        } catch (error) {
          console.error('Failed to create solution:', error);
          void message.error('Failed to create solution');
          setLoading(false);
          return;
        }
      }

      // Create new situation if needed
      if (createNewSituation && values.situationName) {
        try {
          const res = await apiClient.post('/artifacts', {
            name: values.situationName,
            artifact_type: SITUATION_ARTIFACT_TYPE,
            company_id: productId,
            artifact_metadata: {},
          });
          situationId = res.data.id;
        } catch (error) {
          console.error('Failed to create situation:', error);
          void message.error('Failed to create situation');
          setLoading(false);
          return;
        }
      }

      // Create the deal artifact
      const dealRes = await apiClient.post('/artifacts', {
        name: values.name,
        description: values.description,
        artifact_type: DEAL_ARTIFACT_TYPE,
        company_id: productId,
        artifact_metadata: {
          product_solution_id: solutionId || null,
          customer_situation_id: situationId || null,
          customer_actor_id: null,
          connection_links: {
            solutionInputToSituationResourceLinks: [],
            solutionBenefitToSituationNeedLinks: [],
          },
          deal_score: 0,
          product_situation_alignment: 0,
          product_situation_value: 0,
          actor_situation_alignment: 0,
        },
      });

      if (dealRes.data && dealRes.data.id) {
        form.resetFields();
        setCreateNewSolution(false);
        setCreateNewSituation(false);
        onSuccess(dealRes.data.id, values.name);
        void message.success(`Deal "${values.name}" created successfully`);
      } else {
        throw new Error('Failed to create deal - no ID returned');
      }
    } catch (error: unknown) {
      console.error('Failed to create deal:', error);
      void message.error((error instanceof Error ? error.message : String(error)) || 'Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setCreateNewSolution(false);
    setCreateNewSituation(false);
    onCancel();
  };

  return (
    <Modal
      title={
        <div className={styles.modalTitle}>
          <AimOutlined className={styles.titleIcon} />
          <span>New Deal Analysis</span>
        </div>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Create Deal"
      className={styles.modal}
      destroyOnHidden
      width={560}
    >
      <Spin spinning={loadingOptions}>
        <Form
          form={form}
          layout="vertical"
          className={styles.form}
        >
          <Form.Item
            name="name"
            label="Deal Name"
            rules={[
              { required: true, message: 'Please enter a deal name' },
              { min: 2, message: 'Name must be at least 2 characters' },
            ]}
          >
            <Input 
              placeholder="e.g., Enterprise CRM Deal - Acme Corp" 
              autoFocus
              maxLength={100}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              placeholder="Describe the deal (optional)"
              rows={2}
              maxLength={500}
            />
          </Form.Item>

          <Divider className={styles.divider}>
            <SolutionOutlined /> Product Solution
          </Divider>

          {!createNewSolution ? (
            <Form.Item
              name="solutionId"
              label="Select Solution"
            >
              <Select
                placeholder="Select an existing solution"
                options={solutions}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={() => setCreateNewSolution(true)}
                      className={styles.createNewButton}
                    >
                      Create New Solution
                    </Button>
                  </>
                )}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="solutionName"
              label="New Solution Name"
              rules={[
                { required: createNewSolution, message: 'Please enter a solution name' },
              ]}
            >
              <Input 
                placeholder="Enter new solution name"
                suffix={
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => setCreateNewSolution(false)}
                  >
                    Select Existing
                  </Button>
                }
              />
            </Form.Item>
          )}

          <Divider className={styles.divider}>
            <BulbOutlined /> Customer Situation
          </Divider>

          {!createNewSituation ? (
            <Form.Item
              name="situationId"
              label="Select Situation"
            >
              <Select
                placeholder="Select an existing situation"
                options={situations}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={() => setCreateNewSituation(true)}
                      className={styles.createNewButton}
                    >
                      Create New Situation
                    </Button>
                  </>
                )}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="situationName"
              label="New Situation Name"
              rules={[
                { required: createNewSituation, message: 'Please enter a situation name' },
              ]}
            >
              <Input 
                placeholder="Enter new situation name"
                suffix={
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => setCreateNewSituation(false)}
                  >
                    Select Existing
                  </Button>
                }
              />
            </Form.Item>
          )}

          <div className={styles.note}>
            <strong>Note:</strong> The Customer Actor can be selected after creating the deal in the analysis page.
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default NewDealDialog;
